import React from 'react';
import * as R from 'ramda';
import { Typography } from '@mui/material';
import { DocumentNode } from 'apollo-link';
import { Mutation, Query, MutationFunction, QueryResult } from 'react-apollo';
import { FetchPolicy, MutationUpdaterFn, ApolloError } from 'apollo-client';
import { Resource, Item } from '../Utils/genericTypes';
import { computeUpdateMutationVariables } from '../Utils/computeUpdateMutationVariables';
import { isReactComponent } from '../../crud-core/utils/TypescriptUtils';
import { EditContext } from './EditContext';

interface QueryVariables {
  where?: {} | null;
}

interface childrenFunction {
  item: Record<string, any>;
  onSave: (item: {}) => Promise<null | void>;
  resourceConfig: Resource;
  saving: boolean;
}

interface MutationProps {
  mutation: DocumentNode;
  update?: MutationUpdaterFn;
  onCompleted?: (data: Record<string, any>) => void;
  onError?: (error: ApolloError) => void;
}

interface ResourceEditContainerProps {
  messages?: {
    error?: JSX.Element;
    loading?: JSX.Element;
    nocontent?: JSX.Element;
  };
  query: DocumentNode;
  redirectAfterSave?: boolean;
  resourceConfig: Resource;
  variables: QueryVariables;
  isCreate: boolean;
  beforeFetch?: <T>(item: any) => any;
  afterFetch?: <T>(item: any) => any;
  beforeSave?: <T>(item: any) => null | any;
  mutationProps: MutationProps;
  children?:
    | JSX.Element
    | JSX.Element[]
    | (({ item, onSave, resourceConfig, saving }: childrenFunction) => JSX.Element);
  emptyResourceItem?: Item;
  fetchPolicy?: FetchPolicy;
}

interface ResourceEditContainerState {
  saving: boolean;
  hasFetched: boolean;
  data: any | null;
}

class ResourceEditContainer extends React.Component<
  ResourceEditContainerProps,
  ResourceEditContainerState
> {
  readonly state: ResourceEditContainerState = {
    saving: false,
    hasFetched: false,
    data: null,
  };

  onSave =
    (save: MutationFunction, resourceItem: Record<string, any>) =>
    async (item: {}, doFetch = true) => {
      const { resourceConfig, beforeSave, isCreate } = this.props;

      let itemToSave = R.clone(item); // avoid ref from context
      if (beforeSave) {
        itemToSave = beforeSave(itemToSave);
        if (!itemToSave) {
          return null;
        }
      }
      if (doFetch) {
        const mutationVariables = computeUpdateMutationVariables(
          itemToSave,
          resourceConfig,
          resourceItem,
          isCreate,
        );

        if (mutationVariables) {
          this.setState({ saving: true });
          await save({
            variables: mutationVariables,
          });
          this.setState({ saving: false });
        }
      } else {
        this.setState({ saving: false, data: { [resourceConfig.singular]: item } });
      }
    };

  onCompleted = (data: any) => {
    if (!this.state.hasFetched) {
      this.setState({
        data,
        hasFetched: true,
      });
    }
  };

  renderChildren = (
    resourceItem: Record<string, any>,
    onSave: (item: {}) => Promise<null | void>,
  ) => {
    const { children, resourceConfig } = this.props;
    const { saving } = this.state;

    if (typeof children === 'function') {
      return children({ item: resourceItem, resourceConfig, saving, onSave });
    }

    return React.Children.map(children, (child) => {
      if (!child || !isReactComponent(child)) {
        return;
      }

      return React.cloneElement(child, {
        key: child.props.name,
        item: resourceItem,
        resourceConfig,
        saving,
        onSave,
      });
    });
  };

  shouldComponentUpdate(
    nextProps: ResourceEditContainerProps,
    nextState: ResourceEditContainerState,
  ) {
    return (
      !R.equals(this.state.data, nextState.data) ||
      nextState.hasFetched !== this.state.hasFetched ||
      nextProps.isCreate !== this.props.isCreate
    );
  }

  render() {
    const {
      messages,
      mutationProps,
      query,
      resourceConfig,
      variables,
      isCreate,
      afterFetch,
      emptyResourceItem = {},
      fetchPolicy,
    } = this.props;
    return (
      <Query
        query={query}
        variables={variables}
        skip={isCreate || this.state.hasFetched}
        onCompleted={this.onCompleted}
        fetchPolicy={fetchPolicy}
      >
        {({ error, loading }: QueryResult) => {
          if (!isCreate) {
            if (loading) {
              return (messages && messages.loading) || <Typography>Loading.</Typography>;
            }
            if (error) {
              return (messages && messages.error) || <Typography>Error.</Typography>;
            }
            if (!this.state.data || !this.state.data[resourceConfig.singular]) {
              return (messages && messages.nocontent) || <Typography>No content.</Typography>;
            }
          }

          let resourceItem =
            (this.state.data && this.state.data[resourceConfig.singular]) || emptyResourceItem;
          if (afterFetch) {
            resourceItem = afterFetch(resourceItem);
          }
          return (
            <Mutation {...mutationProps}>
              {(save: MutationFunction) => (
                <EditContext.Provider
                  value={{
                    item: resourceItem,
                    resourceConfig,
                    onSave: this.onSave(save, resourceItem),
                    saving: this.state.saving,
                  }}
                >
                  {this.renderChildren(resourceItem, this.onSave(save, resourceItem))}
                </EditContext.Provider>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default ResourceEditContainer;
